import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Property } from '@/types/property';
import { ChevronDown, Search } from 'lucide-react';
import { useMemo, useState } from 'react';

interface PropertiesMultiSelectProps {
  properties: Property[];
  selectedProperties: string[];
  onSelectionChange: (propertyIds: string[]) => void;
}

const PropertiesMultiSelect = ({
  properties,
  selectedProperties,
  onSelectionChange,
}: PropertiesMultiSelectProps) => {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const togglePropertyById = (propertyId: string) => {
    const isSelected = selectedProperties.includes(propertyId);
    if (isSelected) {
      onSelectionChange(selectedProperties.filter((id) => id !== propertyId));
    } else {
      onSelectionChange([...selectedProperties, propertyId]);
    }
  };

  const clearSelection = () => {
    onSelectionChange([]);
    setOpen(false);
  };

  const filteredProperties = useMemo(() => {
    if (!searchTerm.trim()) return properties;
    
    const searchLower = searchTerm.toLowerCase();
    return properties.filter((property) => {
      const shortName = property.shortName?.toLowerCase() || '';
      const title = property.title?.toLowerCase() || '';
      return shortName.includes(searchLower) || title.includes(searchLower);
    });
  }, [properties, searchTerm]);

  const getDisplayText = () => {
    if (selectedProperties.length === 0) {
      return 'Todas las propiedades';
    }
    if (selectedProperties.length === 1) {
      const property = properties.find((p) => p.id === selectedProperties[0]);
      return property?.shortName || property?.title || 'Todas las propiedades';
    }
    return `${selectedProperties.length} propiedades`;
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          className="justify-between min-w-[250px] rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
        >
          <span className="truncate">{getDisplayText()}</span>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>

      <PopoverContent className="w-[280px] p-0" align="start" sideOffset={8}>
        <div className="p-3 border-b">
          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar propiedades..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-8"
            />
          </div>
        </div>
        <div className="max-h-[300px] overflow-y-auto">
          <div className="p-4 space-y-3">
            {filteredProperties.map((property) => (
              <div
                key={property.id}
                className="flex items-center space-x-2 cursor-pointer hover:bg-accent rounded-sm p-2"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  togglePropertyById(property.id);
                }}
                role="checkbox"
                aria-checked={selectedProperties.includes(property.id)}
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.stopPropagation();
                    togglePropertyById(property.id);
                  }
                }}
              >
                <Checkbox
                  id={property.id}
                  checked={selectedProperties.includes(property.id)}
                  tabIndex={-1}
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                  onCheckedChange={(checked) => {
                    const isChecked = checked === true || checked === 'indeterminate';
                    if (isChecked && !selectedProperties.includes(property.id)) {
                      onSelectionChange([...selectedProperties, property.id]);
                    } else if (!isChecked && selectedProperties.includes(property.id)) {
                      onSelectionChange(selectedProperties.filter((id) => id !== property.id));
                    }
                  }}
                />
                <span className="text-sm flex-1">
                  {property.shortName || property.title}
                </span>
              </div>
            ))}
          </div>

          {selectedProperties.length > 0 && (
            <div className="border-t p-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={clearSelection}
                className="w-full"
              >
                Limpiar selección
              </Button>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default PropertiesMultiSelect;
